import React from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LocalText from '../common/LocalText';
import { C } from '../../theme/adminTokens';
import { Locale } from '../../utils/Locales';
import { dropdownMenuProps } from '../../utils/dropdownStyles';
import { formatPeriodRange, mapToValues } from '../../utils/gameSchedule';

// The text in the chosen language, else the first translation there is (what Unity shows when a language is missing),
// flagged so the editor can see it is a fallback.
const pick = (map, lang) => {
  const own = String(map?.[lang] ?? '').trim();
  if (own) return { text: own, fallback: false };
  const first = mapToValues(map)[0];
  return first ? { text: first.value, fallback: true } : { text: '', fallback: false };
};

/**
 * Stage 2 of the Schedule Game popup: how the quiz will look to fans, drawn from the form (no API call). Correct answers
 * are marked for the marketer only — the published quiz never carries them.
 */
export default function QuizPreview({ form, period, lang, localeOptions, onLangChange }) {
  const range = period ? formatPeriodRange(period.start, period.end) : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 760, mx: 'auto', width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography sx={{ fontSize: 13, color: C.muted }}>
          <LocalText>This is how fans will see it. Correct answers are marked for you only.</LocalText>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography component="label" htmlFor="quiz-preview-language" sx={{ fontSize: 11, fontWeight: 700, color: C.muted }}>
            <LocalText>Language</LocalText>
          </Typography>
          <Select
            size="small"
            value={lang}
            onChange={(e) => onLangChange(e.target.value)}
            MenuProps={dropdownMenuProps}
            inputProps={{ id: 'quiz-preview-language', 'aria-label': 'Preview language' }}
            sx={{ minWidth: 150, bgcolor: '#ffffff', borderRadius: '10px', '& .MuiSelect-select': { py: '6px', fontSize: 13 } }}
          >
            {localeOptions.map((loc) => <MenuItem key={loc} value={loc}>{Locale[loc] ?? loc}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      <Box
        data-testid="quiz-preview-card"
        sx={{ bgcolor: '#ffffff', border: `1px solid ${C.border}`, borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: C.navy, color: '#ffffff', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, overflowWrap: 'anywhere' }}>{form.name.trim() || '—'}</Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{range}</Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {form.questions.map((question, qi) => {
            const q = pick(question.text, lang);
            return (
              <Box key={question._key} data-testid="preview-question">
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b', mb: 1, overflowWrap: 'anywhere', fontStyle: q.fallback ? 'italic' : 'normal' }}>
                  <Box component="span" sx={{ color: C.muted, mr: 1 }}>Q{qi + 1}</Box>
                  {q.text || '—'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {question.options.map((option) => {
                    const o = pick(option.text, lang);
                    return (
                      <Box
                        key={option._key}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderRadius: '8px',
                          border: `1px solid ${option.isCorrect ? '#bbf7d0' : C.border}`,
                          bgcolor: option.isCorrect ? '#f0fdf4' : '#ffffff',
                        }}
                      >
                        {option.isCorrect
                          ? <CheckCircleIcon sx={{ fontSize: 18, color: '#16a34a', flexShrink: 0 }} />
                          : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#94a3b8', flexShrink: 0 }} />}
                        <Typography sx={{ fontSize: 13, color: '#1e293b', flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontStyle: o.fallback ? 'italic' : 'normal' }}>
                          {o.text || '—'}
                        </Typography>
                        {option.isCorrect && (
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#15803d', flexShrink: 0 }}>
                            ✓ <LocalText>correct</LocalText>
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
